// navigation/feature-access-panel.tsx
// Admin "Feature access config" — per-user entitlement control. Section-grouped
// feature rows, each with a Live / Locked / Hide segmented control:
//   Live   = normal, navigable nav item
//   Locked = visible in the nav with a lock icon (upsell on click)
//   Hide   = absent from the nav entirely
// Stores only the diffs from the default set as overrides, so setting a feature
// back to its default clears the override.
import { useEffect, useState } from "react";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";
import { authApi, type FeatureState } from "@/api/auth";
import { FEATURES, SECTIONS, type FeatureId } from "./nav-config";

type StateMap = Record<string, FeatureState>;

const STATE_OPTIONS: { value: FeatureState; label: string; color: string; bg: string }[] = [
  { value: "live",   label: "Live",   color: "var(--signal-green)",     bg: "rgba(18,183,106,0.12)" },
  { value: "locked", label: "Locked", color: "var(--signal-blue)",      bg: "var(--signal-blue-10)" },
  { value: "hidden", label: "Hide",   color: "var(--muted-foreground)", bg: "var(--muted)" },
];

const stateFor = (m: StateMap, id: string): FeatureState => m[id] ?? "live";

function diffOverrides(effective: StateMap, defaults: StateMap): StateMap {
  const out: StateMap = {};
  for (const f of FEATURES) {
    if (stateFor(effective, f.id) !== stateFor(defaults, f.id)) out[f.id] = stateFor(effective, f.id);
  }
  return out;
}

export function FeatureAccessPanel({
  userId,
  username,
  onClose,
}: {
  userId: number;
  username: string;
  onClose: () => void;
}) {
  const [effective, setEffective] = useState<StateMap>({});
  const [defaults, setDefaults] = useState<StateMap>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<FeatureId | null>(null);

  useEffect(() => {
    let alive = true;
    authApi
      .getUserFeatureLocks(userId)
      .then((r) => { if (alive) { setEffective(r.effective || {}); setDefaults(r.defaults || {}); } })
      .catch(() => { if (alive) toast.error("Couldn't load feature access."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setFeatureState = async (id: FeatureId, state: FeatureState) => {
    if (stateFor(effective, id) === state) return;
    const prev = effective;
    const next = { ...effective, [id]: state };
    setEffective(next);
    setSavingId(id);
    try {
      await authApi.setUserFeatureLocks(userId, diffOverrides(next, defaults));
    } catch {
      setEffective(prev); // revert
      toast.error("Couldn't save that change.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 flex items-start justify-center" style={{ zIndex: 60, background: "rgba(16,18,22,0.40)", paddingTop: "8vh", padding: 16 }}>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Feature access for ${username}`}
        className="bg-card"
        style={{ width: 420, maxWidth: "94vw", maxHeight: "84vh", overflowY: "auto", border: "1px solid var(--border)", borderRadius: 14, padding: "16px 18px", boxShadow: "var(--elevation-lg)" }}
      >
        <div className="flex items-center" style={{ gap: 8 }}>
          <span className="inline-flex" style={{ color: "var(--signal-blue)" }}><Flag style={{ width: 14, height: 14, strokeWidth: 1.85 }} /></span>
          <span className="flex-1 text-foreground" style={{ fontSize: 12.5, fontWeight: 700 }}>Feature access config</span>
          <button onClick={onClose} aria-label="Close" className="inline-flex items-center justify-center text-muted-foreground" style={{ width: 26, height: 26, border: "none", background: "transparent", borderRadius: 7, cursor: "pointer" }}><X style={{ width: 16, height: 16, strokeWidth: 1.85 }} /></button>
        </div>
        <div className="text-muted-foreground" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 6 }}>
          Set how each feature appears for <strong style={{ color: "var(--foreground)" }}>{username}</strong>: <strong style={{ color: "var(--foreground)" }}>Live</strong> (open), <strong style={{ color: "var(--foreground)" }}>Locked</strong> (shown with a lock), or <strong style={{ color: "var(--foreground)" }}>Hide</strong> (removed from their nav). Setting a feature back to its default clears the override.
        </div>

        {loading ? (
          <div className="text-muted-foreground" style={{ padding: "28px 0", textAlign: "center", fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            {SECTIONS.map((s) => (
              <div key={s.id}>
                <div className="text-muted-foreground" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 7 }}>{s.label}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {FEATURES.filter((f) => f.section === s.id).map((f) => {
                    const Icon = f.icon;
                    const current = stateFor(effective, f.id);
                    const busy = savingId === f.id;
                    return (
                      <div
                        key={f.id}
                        className="flex items-center bg-card"
                        style={{ gap: 10, padding: "8px 11px", border: "1px solid var(--border)", borderRadius: 9, opacity: busy ? 0.6 : 1 }}
                      >
                        <span className="inline-flex shrink-0 text-muted-foreground"><Icon style={{ width: 15, height: 15, strokeWidth: 1.75 }} /></span>
                        <span className="flex-1 text-left text-foreground" style={{ fontSize: 13, fontWeight: 500 }}>{f.label}</span>
                        <div className="inline-flex shrink-0" role="group" aria-label={`${f.label} access`} style={{ gap: 2, padding: 2, background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 8 }}>
                          {STATE_OPTIONS.map((opt) => {
                            const selected = current === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setFeatureState(f.id, opt.value)}
                                disabled={busy}
                                aria-pressed={selected}
                                style={{
                                  fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 6,
                                  border: "none", cursor: busy ? "default" : "pointer", fontFamily: "inherit",
                                  color: selected ? opt.color : "var(--muted-foreground)",
                                  background: selected ? opt.bg : "transparent",
                                  transition: "background 120ms var(--ease-standard), color 120ms var(--ease-standard)",
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
